import { jsx, jsxs } from "react/jsx-runtime";
import { cx } from "@/styled-system/css";
import { Box, FormField, Textarea, FormHelp, Label, Text, Input, Paragraph, Anchor, Alert, Button } from "@zillow/constellation-10";
import { cookiesToZGUID } from "@zillow/mortgage-urls";
import React, { useRef, useEffect } from "react";
import { useForm, useFormState, FormProvider } from "react-hook-form";
import { useCurrentUser } from "../../hooks/user.esm.js";
import { getInvalidEmailError } from "../Fields/EmailField.esm.js";
import { getInvalidNameError } from "../Fields/NameField.esm.js";
import AddressAutocompleteField from "./AddressAutocompleteField.esm.js";
import SuccessState from "./SuccessState.esm.js";
import API_ENDPOINTS from "./constants/api.esm.js";
import { triggerFormSubmitClickstreamEvent, triggerPrivacyPolicyClickstreamEvent, triggerSubmitAnotherReportClickstreamEvent, triggerFormFieldEngagementClickstreamEvent } from "./utils/clickstream.esm.js";
import { ifNullStringNull, getPropertyReportFilledFieldSurfaceCss, propertyReportInputHoverRim, propertyReportInputIdleRim } from "./utils/common.esm.js";
import { useBlockEnvironment } from "../../contexts/BlockEnvironment/BlockEnvironmentProvider.esm.js";
const fieldDescriptionHelpCss = {
  marginTop: "spacing.50",
  color: "FormField.HelperArea.text.default",
  textStyle: "body-xs"
};
const fieldReservedHelperSlotCss = {
  marginTop: "spacing.50",
  minHeight: "spacing.300",
  textStyle: "body-xs",
  color: "FormField.HelperArea.text.default"
};
const propertyReportFormFieldBottomMarginCss = {
  marginBottom: "{spacing.tighter} !",
  marginBlockEnd: "{spacing.tighter} !"
};
const addressAndReportDetailsHelperCss = {
  ...fieldDescriptionHelpCss,
  marginTop: "{spacing.tightest} !",
  marginBlockStart: "{spacing.tightest} !"
};
const optionalLabelHintCss = {
  color: "gray.400"
};
const textEntryPlaceholderCss = {
  _placeholder: {
    color: "gray.600"
  }
};
const propertyReportTextEntryPaddingCss = {
  boxSizing: "border-box",
  paddingBlock: "calc({spacing.200} + {borderWidths.Input.default}) !",
  paddingLeft: "{spacing.400} !",
  paddingRight: "{spacing.400} !",
  paddingInlineStart: "{spacing.400} !",
  paddingInlineEnd: "{spacing.400} !"
};
const propertyReportSingleLineInputHeightCss = {
  boxSizing: "border-box",
  minHeight: "{spacing.1100} !",
  height: "{spacing.1100} !"
};
const propertyReportTextareaHeightCss = {
  boxSizing: "border-box",
  minHeight: "calc({spacing.1200} + {spacing.1100} + {spacing.600}) !",
  height: "calc({spacing.1200} + {spacing.1100} + {spacing.600}) !"
};
const addressInputCssExtra = {
  ...textEntryPlaceholderCss,
  ...propertyReportTextEntryPaddingCss,
  ...propertyReportSingleLineInputHeightCss
};
const API_ERROR_MESSAGE = "There was an error submitting the form. Please try again.";
function scrollToTop(ref) {
  setTimeout(() => {
    var _a;
    (_a = ref.current) == null ? void 0 : _a.scrollIntoView({
      behavior: "smooth"
    });
  }, 0);
}
function triggerFailedSubmit(invalidFields = []) {
  triggerFormSubmitClickstreamEvent(false, invalidFields, false);
}
const PropertyReportTransparency = ({
  id
}) => {
  var _a, _b, _c, _d, _e, _f, _g;
  const {
    environment = "prod"
  } = useBlockEnvironment();
  const apiEndpoint = ((_b = (_a = API_ENDPOINTS) == null ? void 0 : _a[environment]) == null ? void 0 : _b.LISTING_CONTACT) || API_ENDPOINTS.prod.LISTING_CONTACT;
  const methods = useForm({
    mode: "onTouched",
    defaultValues: {
      address: "",
      comments: "",
      mlsName: "",
      userName: "",
      userEmail: ""
    }
  });
  const {
    errors
  } = useFormState({
    control: methods.control
  });
  const {
    data
  } = useCurrentUser();
  const user = (data == null ? void 0 : data.user) && typeof data.user === "object" ? data.user : null;
  const [zpid, setZpid] = React.useState("");
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [referenceNumber, setReferenceNumber] = React.useState("");
  const [apiError, setApiError] = React.useState("");
  const containerRef = useRef(null);
  useEffect(() => {
    if (user) {
      const userName = [user.firstName, user.lastName].filter(Boolean).join(" ");
      methods.setValue("userName", userName);
      methods.setValue("userEmail", user.email);
    }
  }, [methods, user]);
  const handleAddressSelect = (address, zpidValue) => {
    methods.setValue("address", address, {
      shouldValidate: true
    });
    if (zpidValue) {
      setZpid(zpidValue);
    }
  };
  const handleBlur = (label) => () => {
    triggerFormFieldEngagementClickstreamEvent(label);
  };
  const handleSubmitAnotherReport = () => {
    triggerSubmitAnotherReportClickstreamEvent("Submit another report", "", "_self");
    setIsSuccess(false);
    setReferenceNumber("");
    methods.reset({
      address: "",
      comments: "",
      mlsName: "",
      userName: methods.getValues("userName"),
      userEmail: methods.getValues("userEmail")
    });
    setZpid("");
    scrollToTop(containerRef);
  };
  const onSubmit = methods.handleSubmit(async (data2) => {
    var _a2;
    setApiError("");
    setIsSubmitting(true);
    const encodedZuid = ifNullStringNull(cookiesToZGUID(document.cookie)) || "";
    const formData = {
      ...data2,
      zpid,
      encodedZuid
    };
    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        const data3 = await response.json();
        if (((_a2 = data3 == null ? void 0 : data3.message) == null ? void 0 : _a2.code) === 200) {
          setReferenceNumber(data3 == null ? void 0 : data3.key);
          triggerFormSubmitClickstreamEvent(true, [], true, "");
          setIsSuccess(true);
          setApiError("");
          scrollToTop(containerRef);
        } else {
          setApiError(API_ERROR_MESSAGE);
          triggerFailedSubmit();
        }
      } else {
        setApiError(API_ERROR_MESSAGE);
        triggerFailedSubmit();
      }
    } catch (e) {
      setApiError(API_ERROR_MESSAGE);
      triggerFailedSubmit();
    }
    setIsSubmitting(false);
  }, (errors2) => {
    const invalidFields = Object.keys(errors2);
    triggerFailedSubmit(invalidFields);
    setIsSubmitting(false);
  });
  if (isSuccess) {
    return /* @__PURE__ */ jsx(SuccessState, { referenceNumber, onSubmitAnother: handleSubmitAnotherReport });
  }
  return /* @__PURE__ */ jsx(FormProvider, { ...methods, children: /* @__PURE__ */ jsxs(Box, { id, ref: containerRef, children: [
    /* @__PURE__ */ jsx("input", { type: "hidden", ...methods.register("address", {
      required: {
        value: true,
        message: "Property address is required"
      }
    }) }),
    /* @__PURE__ */ jsx(AddressAutocompleteField, { onSelect: handleAddressSelect, required: true, label: "Property address", error: (_c = errors == null ? void 0 : errors.address) == null ? void 0 : _c.message, placeholder: "Enter complete property address with street, city, state, and ZIP code", description: "Enter the complete property address for the listing with the violation.", onBlur: handleBlur("Property address"), inputCssExtra: addressInputCssExtra, formHelpCss: addressAndReportDetailsHelperCss }),
    /* @__PURE__ */ jsx(Box, { "aria-hidden": true, css: {
      display: "block",
      marginBottom: "{spacing.tighter}",
      marginBlockEnd: "{spacing.tighter}"
    } }),
    /* @__PURE__ */ jsx(FormField, { error: Boolean(errors.comments), css: {
      display: "flex",
      flexDirection: "column",
      gap: 0,
      overflow: "visible",
      marginBottom: "0 !",
      marginBlockEnd: "0 !"
    }, label: /* @__PURE__ */ jsxs(Label, { htmlFor: "comments", css: {
      paddingBottom: "spacing.50",
      fontWeight: "fontWeights.bold",
      color: "text.neutral"
    }, children: [
      "Report details",
      " ",
      /* @__PURE__ */ jsx(Text, { textStyle: "body-sm", css: optionalLabelHintCss, children: "(optional)" })
    ] }), description: /* @__PURE__ */ jsx(FormHelp, { error: Boolean(errors.comments), css: addressAndReportDetailsHelperCss, children: ((_d = errors.comments) == null ? void 0 : _d.message) || "Include any details about the listing or the violation that would be helpful." }), control: /* @__PURE__ */ jsx(Textarea, { ...methods.register("comments", {
      onBlur: handleBlur("Report details")
    }), id: "comments", className: cx(propertyReportInputHoverRim, propertyReportInputIdleRim), placeholder: "Any information that might help our investigation, including a web address or URL if relevant.", rows: 4, error: Boolean(errors.comments), css: {
      ...getPropertyReportFilledFieldSurfaceCss(Boolean(errors.comments)),
      ...textEntryPlaceholderCss,
      ...propertyReportTextEntryPaddingCss,
      ...propertyReportTextareaHeightCss
    } }) }),
    /* @__PURE__ */ jsx(Box, { "aria-hidden": true, css: {
      display: "block",
      marginBottom: "{spacing.tighter}",
      marginBlockEnd: "{spacing.tighter}"
    } }),
    /* @__PURE__ */ jsx(FormField, { required: true, error: Boolean(errors.userName), css: {
      display: "flex",
      flexDirection: "column",
      gap: 0,
      ...propertyReportFormFieldBottomMarginCss
    }, label: /* @__PURE__ */ jsx(Label, { htmlFor: "userName", css: {
      paddingBottom: "spacing.50",
      fontWeight: "fontWeights.bold",
      color: "text.neutral"
    }, children: "Your name" }), description: ((_e = errors.userName) == null ? void 0 : _e.message) ? /* @__PURE__ */ jsx(FormHelp, { error: Boolean(errors.userName), css: fieldDescriptionHelpCss, children: errors.userName.message }) : /* @__PURE__ */ jsx(FormHelp, { css: fieldReservedHelperSlotCss, children: " " }), control: /* @__PURE__ */ jsx(Input, { ...methods.register("userName", {
      validate: (value) => {
        const err = getInvalidNameError(value == null ? "" : String(value));
        return err === null ? true : err;
      },
      onBlur: handleBlur("Your name")
    }), id: "userName", className: cx(propertyReportInputHoverRim, propertyReportInputIdleRim), type: "text", autoComplete: "name", placeholder: "Enter your full name", error: Boolean(errors.userName), css: {
      ...getPropertyReportFilledFieldSurfaceCss(Boolean(errors.userName)),
      ...textEntryPlaceholderCss,
      ...propertyReportTextEntryPaddingCss,
      ...propertyReportSingleLineInputHeightCss
    } }) }),
    /* @__PURE__ */ jsx(FormField, { required: true, error: Boolean(errors.userEmail), css: {
      display: "flex",
      flexDirection: "column",
      gap: 0,
      ...propertyReportFormFieldBottomMarginCss
    }, label: /* @__PURE__ */ jsx(Label, { htmlFor: "userEmail", css: {
      paddingBottom: "spacing.50",
      fontWeight: "fontWeights.bold",
      color: "text.neutral"
    }, children: "Your email" }), description: ((_f = errors.userEmail) == null ? void 0 : _f.message) ? /* @__PURE__ */ jsx(FormHelp, { error: Boolean(errors.userEmail), css: fieldDescriptionHelpCss, children: errors.userEmail.message }) : /* @__PURE__ */ jsx(FormHelp, { css: fieldReservedHelperSlotCss, children: " " }), control: /* @__PURE__ */ jsx(Input, { ...methods.register("userEmail", {
      required: "Please enter your email",
      validate: (value) => {
        const err = getInvalidEmailError(value == null ? "" : String(value));
        return err === null ? true : err;
      },
      onBlur: handleBlur("Your email")
    }), id: "userEmail", className: cx(propertyReportInputHoverRim, propertyReportInputIdleRim), type: "email", autoComplete: "email", placeholder: "your.email@example.com", error: Boolean(errors.userEmail), css: {
      ...getPropertyReportFilledFieldSurfaceCss(Boolean(errors.userEmail)),
      ...textEntryPlaceholderCss,
      ...propertyReportTextEntryPaddingCss,
      ...propertyReportSingleLineInputHeightCss
    } }) }),
    /* @__PURE__ */ jsx(FormField, { error: Boolean(errors.mlsName), css: {
      display: "flex",
      flexDirection: "column",
      gap: 0,
      ...propertyReportFormFieldBottomMarginCss
    }, label: /* @__PURE__ */ jsxs(Label, { htmlFor: "mlsName", css: {
      paddingBottom: "spacing.50",
      fontWeight: "fontWeights.bold",
      color: "text.neutral"
    }, children: [
      "MLS name",
      " ",
      /* @__PURE__ */ jsx(Text, { textStyle: "body-sm", css: optionalLabelHintCss, children: "(optional)" })
    ] }), description: ((_g = errors.mlsName) == null ? void 0 : _g.message) ? /* @__PURE__ */ jsx(FormHelp, { error: Boolean(errors.mlsName), css: fieldDescriptionHelpCss, children: errors.mlsName.message }) : /* @__PURE__ */ jsx(FormHelp, { css: fieldReservedHelperSlotCss, children: " " }), control: /* @__PURE__ */ jsx(Input, { ...methods.register("mlsName", {
      onBlur: handleBlur("MLS name")
    }), id: "mlsName", className: cx(propertyReportInputHoverRim, propertyReportInputIdleRim), type: "text", placeholder: "Enter MLS name", error: Boolean(errors.mlsName), css: {
      ...getPropertyReportFilledFieldSurfaceCss(Boolean(errors.mlsName)),
      ...textEntryPlaceholderCss,
      ...propertyReportTextEntryPaddingCss,
      ...propertyReportSingleLineInputHeightCss
    } }) }),
    /* @__PURE__ */ jsxs(Paragraph, { textStyle: "fineprint-sm", color: "text.neutral", children: [
      "We respect your privacy—your personal information will only be used to follow up on this report and will not be sold or shared with third parties. The information you provide will help us enforce Zillow's listing access standards. By submitting your contact information, you agree that Zillow Group may contact you to follow up or verify the details you provided. Submitting this form does not create a contractual relationship or guarantee a response. For more information, please review our",
      " ",
      /* @__PURE__ */ jsx(Anchor, { href: "https://www.zillow.com/privacy/", target: "_blank", rel: "noopener noreferrer", onClick: () => triggerPrivacyPolicyClickstreamEvent("Privacy Policy", "https://www.zillow.com/privacy/", "_blank"), children: "Privacy Policy" }),
      "."
    ] }),
    /* @__PURE__ */ jsx(Box, { css: {
      marginBottom: "loose"
    } }),
    apiError && /* @__PURE__ */ jsx(Box, { css: {
      marginBottom: "default"
    }, children: /* @__PURE__ */ jsx(Alert, { tone: "critical", role: "alert", children: apiError }) }),
    /* @__PURE__ */ jsx(Button, { tone: "brand", emphasis: "filled", loading: isSubmitting, disabled: isSubmitting, onClick: onSubmit, children: "Submit report" })
  ] }) });
};
export {
  PropertyReportTransparency as default
};
